import { useEffect, useRef } from 'react';
import { useSoundContext } from '../contexts/sound-context';
import { displayToast } from '../helpers/display-toast';
import { useGetUserQuery } from '../slices';
import { useUpdateSoundPreferencesMutation } from '../slices/sound-preferences';

export const useSoundObserver = () => {
  const { effectsEnabled, setEffectsEnabled } = useSoundContext();
  const [updateSoundPreferences] = useUpdateSoundPreferencesMutation();
  const prevEffectsEnabledRef = useRef(effectsEnabled);
  const prevSavedEffectsEnabledRef = useRef<boolean | undefined>(undefined);
  const { data, isSuccess: isUserQuerySuccess } = useGetUserQuery();

  const savedEffectsEnabled = data?.data?.user?.settings?.sound?.effectsEnabled;

  const canModifySoundPreferences = isUserQuerySuccess === true && typeof setEffectsEnabled === 'function';

  useEffect(() => {
    if (!canModifySoundPreferences) return;
    if (savedEffectsEnabled === undefined) return;

    if (
      prevSavedEffectsEnabledRef.current === undefined ||
      prevSavedEffectsEnabledRef.current !== savedEffectsEnabled
    ) {
      setEffectsEnabled(savedEffectsEnabled);
      prevSavedEffectsEnabledRef.current = savedEffectsEnabled;
    }
  }, [canModifySoundPreferences, savedEffectsEnabled, setEffectsEnabled]);

  useEffect(() => {
    let unmounted = false;

    const update = async () => {
      if (canModifySoundPreferences && effectsEnabled !== prevEffectsEnabledRef.current) {
        try {
          prevEffectsEnabledRef.current = effectsEnabled;
          await updateSoundPreferences(effectsEnabled).unwrap();
        } catch (error) {
          if (unmounted) return;
          prevEffectsEnabledRef.current = !effectsEnabled;
          console.error('Failed to save sound preferences:', error);
          displayToast('error', 'Failed to save sound preferences');
        }
      }
    };

    // eslint-disable-next-line no-void
    void update();
    return () => {
      unmounted = true;
    };
  }, [canModifySoundPreferences, effectsEnabled, updateSoundPreferences]);
};
